import {
    createApiClient
} from './';
import {
    onResponseLogging, onResponseErrorLogging
} from '../../utils/loggingUtils';
import type {
 AxiosInstance
} from 'axios';

export const BEX_KB_API_BASE_URL: string = process.env.REACT_APP_KB_SERVER_URL || '';
const BEX_KB_API_ENDPOINT_PREFIX = '/v2/kb/api/v1';

/**
 * HTTP client for the Project O API endpoints /  services.
 *
 * @type {ApiClient}
 */
const knowledgeBuilderHttpClient: AxiosInstance = createApiClient(
    BEX_KB_API_BASE_URL,
    BEX_KB_API_ENDPOINT_PREFIX,
    undefined,
    onResponseLogging,
    undefined,
    onResponseErrorLogging
);
const knowledgeBuilderHttpClientWithCreds: AxiosInstance = createApiClient(
    BEX_KB_API_BASE_URL,
    BEX_KB_API_ENDPOINT_PREFIX,
    undefined,
    onResponseLogging,
    undefined,
    onResponseErrorLogging
);

knowledgeBuilderHttpClient.defaults.withCredentials = false;
knowledgeBuilderHttpClientWithCreds.defaults.withCredentials = false;

export { knowledgeBuilderHttpClient, knowledgeBuilderHttpClientWithCreds };

const BEX_WEBSOCKET_HOST: string = process.env.REACT_APP_KB_WEB_SOCKET_URL || '';
const BEX_WEBSOCKET_PREFIX = '/v2/kb/api/v1';
const BEX_KB_WEBSOCKET_URL = `${BEX_WEBSOCKET_HOST}${BEX_WEBSOCKET_PREFIX}`;

interface WebSocketOptions {
    onOpen?: (event: Event) => void;
    onMessage?: (message: MessageEvent) => void;
    onError?: (error: Event) => void;
    onClose?: (event: CloseEvent) => void;
}

interface ExtendedWebSocket extends WebSocket {
    sendMessage: (data: string | ArrayBuffer | Blob | ArrayBufferView) => void;
}

/**
 * A Dynamic WebSocket client maker with event interceptors.
 * @param {string} endpoint - The specific WebSocket endpoint to connect to (e.g., "/notifications").
 * @param {Object} [options={}] - Options to configure WebSocket events.
 * @param {Function} [options.onOpen] - Optional function to be called on WebSocket open event.
 * @param {Function} [options.onMessage] - Optional function to be called on WebSocket message event.
 * @param {Function} [options.onError] - Optional function to be called on WebSocket error event.
 * @param {Function} [options.onClose] - Optional function to be called on WebSocket close event.
 * @returns {WebSocket} The WebSocket instance for managing connections.
 */
export const createWebSocketClient = (
    endpoint: string,
    {
        onOpen = () => {},
        onMessage = () => {},
        onError = (error: Event) => console.error('WebSocket error:', error),
        onClose = () => {}
    }: WebSocketOptions = {}
): ExtendedWebSocket => {
    // Construct WebSocket URL by combining base URL, prefix, and endpoint
    const wsURL = `${BEX_KB_WEBSOCKET_URL}${endpoint}`;

    // Create WebSocket instance
    const wsClient = new WebSocket(wsURL) as ExtendedWebSocket;

    // Set up WebSocket event listeners with optional interceptors
    wsClient.onopen = (event: Event) => onOpen(event);
    wsClient.onmessage = (message: MessageEvent) => onMessage(message);
    wsClient.onerror = (error: Event) => onError(error);
    wsClient.onclose = (event: CloseEvent) => onClose(event);

    /**
     * Function to send a message to the WebSocket server.
     * @param {string | ArrayBuffer | Blob | ArrayBufferView} data - The data to be sent.
     */
    wsClient.sendMessage = (data: string | ArrayBuffer | Blob | ArrayBufferView): void => {
        if (wsClient.readyState === WebSocket.OPEN) {
            wsClient.send(data);
        } else {
            console.warn('WebSocket is not open. Unable to send message.');
        }
    };

    return wsClient;
};
